<template lang="html">
  <div class="row encounter-slide">
    <div v-if="treatment_plan" class="tp-title col-12">
      <h5><strong>{{ treatment_plan.Heading }}</strong></h5>
      <p><em>
        treatment plan prepared 
        <span v-if="pat_name">for {{ pat_name }}</span> 
        <span v-if="treatment_plan.SecDateTEdit"> on {{ treatment_plan.SecDateTEdit | prettyDateShortFromDT }}</span>
      </em></p>
    </div> <!-- /.tp-title -->

    <div class="slide-body col-10 offset-1">
      
      <div class="row row-totals justify-content-center">
        <div class="col-4">
          <h3>Treatment Plan Total</h3>
          <div class="total total-tp">
            <h3 v-if="procsCostTot !== null">{{ procsCostTot | prettyPrice }}</h3>
            <h3 v-else>--</h3>
          </div>
        </div> <!-- /.col-4 -->
        <div class="col-4">
          <h3><strong>Est. After Benefits</strong></h3>
          <div class="total total-ab">
            <h3 v-if="procsABTot !== null">{{ procsABTot  | prettyPrice}}</h3>
            <h3 v-else>--</h3>
          </div>
        </div> <!-- /.col-4 -->
        <div v-if="futureSavingsTot !== null" class="col-4">
          <h3>Saved on Future Care</h3>
          <div class="total total-future">
            <h3>${{ futureSavingsTot }}</h3>
          </div>
        </div> <!-- /.col-4 -->
      </div> <!-- /.row -->

      <div class="row row-options">
        <div class="col-12 text-center"><h3><strong>Cost Saving Options</strong></h3></div>
        <div class="col-4">
          <div class="box-option d-flex flex-column align-items-center justify-content-center">
            <p>No Insurance?</p>
            <h3>Save Up To 45% with a Membership</h3>
          </div>
        </div> <!-- /.col-4 -->
        <div class="col-4">
          <div class="box-option d-flex flex-column align-items-center justify-content-center">
            <h3>Save 10%</h3>
            <p>
              Advancing Disease Care<br>
              Same or Next Day
            </p>
          </div>
        </div> <!-- /.col-4 -->
        <div class="col-4">
          <div class="box-option d-flex flex-column align-items-center justify-content-center">
            <p>Financing Options</p>
            <h3>
              Monthly Payment Plans 
              <span v-if="pymtPlanMin!==null && pymtPlanMax!==null">${{ pymtPlanMin }}-{{ pymtPlanMax }}/mo</span>
            </h3>
            <p class="sm">1-5 yr terms available.<span v-if="pymtPlanMin!==null && pymtPlanMax!==null"> Estimate only.</span></p>
          </div>
        </div> <!-- /.col-4 -->
      </div> <!-- /.row -->
      
      <hr class="hr-dark-color" />
      <p class="saving" v-if="saving"><i class="far fa-fw fa-pulse fa-spinner"></i> saving, please wait...</p>
      <p class="text-center" v-if="!saving"><button @click.prevent="markSlideAndForward" class="btn btn-green btn-lg">Yes, I'm Ready To Start!<i class="fas fa-angle-right fa-fw"></i></button></p>
    </div> <!-- /.slide-body -->

  </div> <!-- /.encounter-slide -->
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import ImageGallery from '../components/ImageGallery.vue'

export default {
  props: ['encounterData'],
  components: {
    ImageGallery,
  },
  data() {
    return {
      saving: false,
    };
  },
  computed: {
    ...mapGetters(['user', 'user_props', 'custom_claims', 'appointment', 'encounter','treatment_plan', 'pat_num', 'pat_name']),
    procsCostTot() {
      if(this.encounterData && this.encounterData.treatPlanData && this.encounterData.treatPlanData.length && this.encounterData.locationProcCats) {
        // before reducing, filter treatPlanData to only include procedures that are in the locationProcCats with category_code values of advancing, rebuilding, or protection
        const filteredTreatPlanData = this.encounterData.treatPlanData.filter(tp => {
          const procCat = this.encounterData.locationProcCats[tp.ProcCode];
          return procCat && ['advancing','rebuilding','protection'].includes(procCat.category_code);
        });
        return filteredTreatPlanData.reduce((tot,next) => tot + next.FeeAmt, 0);
      }
      return null;
    },
    procsABTot() {
      if(this.encounterData && this.encounterData.treatPlanData && this.encounterData.treatPlanData.length && this.encounterData.locationProcCats) {
          // use PatAmt
          const filteredTreatPlanData = this.encounterData.treatPlanData.filter(tp => {
            const procCat = this.encounterData.locationProcCats[tp.ProcCode];
            return procCat && ['advancing','rebuilding','protection'].includes(procCat.category_code);
          });
          return filteredTreatPlanData.reduce((tot,next) => tot + next.PatAmt, 0);
        }
      return null;
    },
    pymtPlanMin() {
      if(this.procsABTot !== null) {
        return Math.ceil(this.procsABTot / 60); // 5 year plan
      }
      return null;
    },
    pymtPlanMax() {
      if(this.procsABTot !== null) {
        return Math.ceil(this.procsABTot / 12); // 1 year plan
      }
      return null;
    },
    futureSavingsTot() {
      if(this.encounterData && this.encounterData.treatPlanData && this.encounterData.treatPlanData.length && this.encounterData.locationProcCats) {
        let tot = 0;
          // calculate future savings based on presence of key procedure codes
          let codesSaveRootCanal = ['D2140','D2150','D2160','D2161','D2330','D2331','D2332','D2335','D2391','D2392','D2393','D2394','D2921','D2940'];
          let codesSaveCrown = ['D3310','D3320','D3330','D3332','D3346','D3347','D3348'];
          let codesSaveImplant = ['D2970','D6795','D4230','D4231','D4249','D6752','D6793','D6058','D6059','D6060','D6061','D6062','D6063','D6064','D6065','D6066','D6067','D6082','D6083','D6084','D6085','D6086','D6087','D6088','D6194','D6753','D2390','D2710','D2712','D2720','D2721','D2722','D2740','D2750','D2751','D2752','D2753','D2780','D2781','D2782','D2783','D2790','D2791','D2792','D2794','D2799','D6710','D6720','D6721','D6722','D6750','D6751','D6780','D6781','D6782','D6783','D6784','D6790','D6791','D6792','D6794','CosmCrownPrem'];
          // if code for filling, add amt for root canal
          if(this.encounterData.treatPlanData.some(tp => codesSaveRootCanal.includes(tp.ProcCode))) {
            tot += 1500; // estimated future cost savings
          }
          // if code for root canal, add amt for crown
          if(this.encounterData.treatPlanData.some(tp => codesSaveCrown.includes(tp.ProcCode))) {
            tot += 1800; // estimated future cost savings
          }
          // if code for crown, add amt for implant
          if(this.encounterData.treatPlanData.some(tp => codesSaveImplant.includes(tp.ProcCode))) {
            tot += 6000; // estimated future cost savings
          }
          return tot;
        }
      return null;
    },
  },
  watch: {
    
  },
  methods: {
    markSlideAndForward() {
      // this.$emit('update-encounter-data', {
      //   slides_completed: [
      //     ...this.encounterData.slides_completed,
      //     this.$route.name,
      //   ],
      // });
      this.$emit('forward')
    },
    // async updateEncounter() {
    //   // exit if missing data
    //   if(!this.custom_claims || !this.custom_claims.customerId || !this.custom_claims.authorized_location) {
    //     console.log('missing data to save appointment encounters');
    //     return;
    //   }
    //   this.saving = true;
    //   // construct encounter object
    //   const encounterObject = {
    //     ...this.encounter,
    //     total_future_savings: this.futureSavingsTot,
    //     total_tp_cost: this.procsCostTot,
    //   };
    //   // update in firestore
    //   let encounterRef = Vue.firestore.doc(`/customers/${this.custom_claims.customerId}/locations/${this.custom_claims.authorized_location}/encounters/${encounterObject.encounter_id}`);
    //   try {
    //     await encounterRef.update(encounterObject);
    //     // console.log('encounter updated successfully');
    //     this.saving = true;
    //   } catch (err) {
    //     console.error('Failed to update encounter during save progress:', err);
    //   }
    //   this.saving = false;
    //   // update encounter in store
    //   this.$store.dispatch('updateEncounter',encounterObject);
    //   // proceed to next step
    //   // this.$emit('forward');
    //   this.markSlideAndForward();
    // },
  },
}
</script>

<style lang="less" scoped>
@import "../assets/less/variables.less";

h1 {
  margin-bottom: 20px;
}
.tp-title {
  text-align: center;
  padding: 20px;
  border-bottom: 1px solid @dark-color;
  margin-bottom: 30px;

  h5,p {
    margin: 0;
  }
}
h5 {
  margin-bottom: 30px;
}
.encounter-slide {
  padding: 0;
}
.slide-body {
  padding: 30px;
}
hr {
  margin: 30px 0;
}
.row-totals {
  text-align: center;
  margin-bottom: 50px;

  h3 {
    margin: 0 0 10px;
  }
}
.total {
  border-top: 2px solid @dark-color;
  padding: 10px 20px;

  h3 {
    margin: 0;
  }
}
.total.total-tp {
  background-color: @color-salmon;
}
.total.total-ab {
  border: 1px solid @dark-color;
  border-top-width: 2px;

  h3 {
    font-weight: bold;
  }
}
.total.total-future {
  background-color: @color-green;
}
.box-option {
  background-color: fadeout(@major-color, 0.5);
  padding: 20px 30px;
  height: 100%;
  min-height: 100%;
  text-align: center;

  p, h3 {
    margin: 0 0 5px;
  }
  h3 {
    font-weight: bold;
  }
  > :last-child {
    margin-bottom: 0;
  }
}
</style>
